import { MLDebug } from './MLDebug';
import { Endpoints, ServiceOptions, SwapCommendationsResult, Events } from './types';
import { InsightsApi, IpexMomentEnum } from '@insights/insights-data-provider';

const ML_QA_API_ROOT = 'https://qa.ml.ipex-insights.com/';
const ML_PROD_API_ROOT = 'https://ml.ipex-insights.com/';

const INSIGHTS_ML_QA_API_KEY = '91qy003gy9ij8q2c';
const INSIGHTS_ML_PROD_API_KEY = '410ttrsjjdbrle4t';

/**
 * This class contains functions for fetching data from
 * Insights ML API.
 */
export class MLService {
  longTermId: string;
  isKiosk: boolean;
  dev: boolean;
  dexfApiKey: string;
  cookieConsent: boolean;
  insightsApi: InsightsApi;
  apiRoot: string;
  countryCode: string;

  constructor(options: ServiceOptions, insightsApi: InsightsApi) {
    const id = localStorage.getItem('long_term_id');
    const longTermId = id ? JSON.parse(id) : { id: '' };

    this.insightsApi = insightsApi;
    this.longTermId = longTermId?.id;
    this.isKiosk = options.isKiosk;
    this.dev = options.dev;
    this.cookieConsent = options.cookieConsent;
    this.dexfApiKey = options.dexfApiKey;
    this.countryCode = options.countryCode;
    this.apiRoot = this.dev ? ML_QA_API_ROOT : ML_PROD_API_ROOT;

    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.fetchSwapCommendations = this.fetchSwapCommendations.bind(this);
    this.startModuleAnalytics();
    new MLDebug();
  }

  private getOptions() {
    const headers: any = {
      'Content-Type': 'application/json',
      'x-long-term-id': this.longTermId,
      'x-kiosk': this.isKiosk,
      'X-API-Key': this.dev ? INSIGHTS_ML_QA_API_KEY : INSIGHTS_ML_PROD_API_KEY
    };

    return {
      credentials: 'same-origin',
      headers
    };
  }

  private async get(root: string, endpoint: string, params?: Record<string, any>) {
    const options: any = { method: 'GET', ...this.getOptions() };
    const queryParams = Object.entries(params || {}).reduce(
      (acc, [param, value], i) => acc.concat(`${i !== 0 ? '&' : ''}${param}=${value}`),
      '?'
    );
    const startTime = new Date();
    const response = await fetch(`${root}${endpoint}${queryParams}`, options);

    const result = await response.json();
    const latency = Math.abs((new Date().getTime() - startTime.getTime()) / 1000);
    this.trackApiCalled({
      latency,
      endpoint,
      ...result
    });
    MLDebug.log(endpoint, result);
    return result;
  }

  private async post(endpoint: string, body?: any, root = this.apiRoot) {
    const options: any = { method: 'POST', ...this.getOptions(), body: JSON.stringify(body) };
    const startTime = new Date();
    const response = await fetch(`${root}${endpoint}`, options);
    const result = await response.json();
    const latency = Math.abs((new Date().getTime() - startTime.getTime()) / 1000);
    this.trackApiCalled({
      latency,
      endpoint,
      ...result['response_meta']
    });
    MLDebug.log(endpoint, result);
    return result;
  }

  /**
   *
   * @param articlesInScene product refs of all articles in scene
   * @param articlesInSelectedFrame product refs of all articles in frame of selected entity
   * @param selectedArticle product ref of selected article
   * @returns array of recommended product refs
   */
  async fetchSwapCommendations(
    articlesInScene: string[],
    articlesInSelectedFrame: string[],
    selectedArticle: string,
    filter: string[],
    numberOfRecommendations = 100
  ) {
    const result: SwapCommendationsResult = await this.post(Endpoints.SWAP_COMMEDATIONS, {
      data: {
        articles_in_scene: articlesInScene,
        articles_in_selected_frame: articlesInSelectedFrame,
        article_selected: selectedArticle
      },
      options: {
        count: numberOfRecommendations,
        filter_set: filter
      }
    });

    return result.recommended ?? [];
  }

  /**
   * Starts the insights module
   */
  private startModuleAnalytics(): void {
    if (this.insightsApi.isEnabled && (this.cookieConsent || this.dev)) {
      try {
        this.insightsApi.moduleClient({
          moduleId: 'ml_service',
          moduleVersion: '0.0',
          dev: this.dev
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize ML Service analytics', e);
      }
    }
  }

  /**
   * Send analytics event
   */
  private trackApiCalled(payload: Record<string, any>): void {
    this.insightsApi.sendEvent({
      ipexMoment: IpexMomentEnum.showMe,
      event: Events.API_CALL,
      payload
    });
  }
}
